import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Card, Button, ProgressBar, Alert, Nav, Form,} from "react-bootstrap";
import OlaCard from "./OlaCard";
import PhaseCard from "./PhaseCard";
import TaskCard from "./TaskCard";
import useHoewistSubmission from "./useHoewistData";

function SaveButton({ unsaved, saving, onSave, size }) {
  return (
    <Button
      size={size}
      variant={unsaved ? "primary" : "outline-secondary"}
      onClick={onSave}
      disabled={saving}
    >
      {saving
        ? <><i className="fad fa-spinner-third fa-spin" />&nbsp;Opslaan...</>
        : unsaved
          ? <><i className="far fa-save" />&nbsp;Opslaan</>
          : <><i className="far fa-check" />&nbsp;Opgeslagen</>
      }
    </Button>
  );
}

const ProgressCard = forwardRef(function ProgressCard({ label, filled, total, percent }, ref) {
  return (
    <Card ref={ref} className="mb-4 hoewist-progress-card">
      <Card.Body className="hoewist-progress-card-body">
        <Row className="align-items-md-center g-2">
          <Col xs={12} md="auto">
            <strong>{label}</strong>
            <div className="hoewist-progress-count">
              {filled} / {total} ingevuld
            </div>
          </Col>
          <Col xs={12} md>
            <ProgressBar
              now={percent}
              label={`${percent}%`}
              variant={
                percent === 100 ? "success"
                  : percent >= 50 ? "warning"
                    : "danger"
              }
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
});

function UnfilledFilterButton({ active, onToggle, labelAll, labelUnfilled }) {
  return (
    <div className="mb-3">
      <Button
        variant={active ? "warning" : "outline-secondary"}
        size="sm"
        onClick={onToggle}
      >
        <i className={`far fa-${active ? "eye-slash" : "eye"} me-1`} />
        {active ? labelAll : labelUnfilled}
      </Button>
    </div>
  );
}

const semesterOrder = s => s === 1 ? 0 : s === 2 ? 1 : 2;

function Hoewist() {
  const {
    loading, saving,
    teacher, programmes, allOLAs, myTasks, newTasks,
    allSubmissions, selectedSubmissionId, academicYears,
    noAcademicYear, hoewistOpen,
    olaPrefs, phasePrefs, taskPrefs,
    unsaved,
    updateOlaPref, updatePhasePref, updateTaskPref,
    switchYear, save,
  } = useHoewistSubmission();

  const [activeTab, setActiveTab] = useState(null);
  const [filterIds, setFilterIds] = useState(null);
  const progressCardRef = useRef(null);
  const [showCompactProgress, setShowCompactProgress] = useState(false);

  const isTaskTab = activeTab === "taken";
  const isContextTab = activeTab === "contexten";

  useEffect(() => {
    if (activeTab === null && programmes.length > 0) {
      setActiveTab(programmes[0].ID);
    }
  }, [programmes, activeTab]);

  const olasForProgramme = useMemo(() =>
    allOLAs
      .filter(ola => ola.programmeID === activeTab)
      .sort((a, b) => {
        if (a.isAssigned !== b.isAssigned) return a.isAssigned ? -1 : 1;
        const semDiff = semesterOrder(a.semester) - semesterOrder(b.semester);
        if (semDiff !== 0) return semDiff;
        return a.name.localeCompare(b.name, "nl");
      }),
    [allOLAs, activeTab]
  );

  const allPhases = useMemo(() => {
    const programmeMap = Object.fromEntries(programmes.map(p => [p.ID, p.name]));
    const seen = new Map();
    allOLAs.forEach(ola => {
      (ola.phases || []).forEach(phase => {
        if (!seen.has(phase.phaseID))
          seen.set(phase.phaseID, { ...phase, olasByProgramme: {} });
        const entry = seen.get(phase.phaseID);
        const programmeName = (ola.programmeID && programmeMap[ola.programmeID]) || null;
        if (programmeName && ola.name) {
          if (!entry.olasByProgramme[programmeName])
            entry.olasByProgramme[programmeName] = [];
          if (!entry.olasByProgramme[programmeName].includes(ola.name))
            entry.olasByProgramme[programmeName].push(ola.name);
        }
      });
    });
    return Array.from(seen.values())
      .sort((a, b) => {
        const aProg = Object.keys(a.olasByProgramme).sort((x, y) => x.localeCompare(y, "nl"))[0] ?? "";
        const bProg = Object.keys(b.olasByProgramme).sort((x, y) => x.localeCompare(y, "nl"))[0] ?? "";
        const progCmp = aProg.localeCompare(bProg, "nl");
        if (progCmp !== 0) return progCmp;
        return (a.phaseName ?? "").localeCompare(b.phaseName ?? "", "nl");
      });
  }, [allOLAs, programmes]);

  const visibleOlas = useMemo(() =>
    olasForProgramme.filter(ola => filterIds === null || filterIds.has(ola.ID)),
    [olasForProgramme, filterIds]
  );
  const visibleAssignedOlas = useMemo(() =>
    visibleOlas.filter(o => o.isAssigned), [visibleOlas]
  );
  const visibleUnassignedOlas = useMemo(() =>
    visibleOlas.filter(o => !o.isAssigned), [visibleOlas]
  );
  const visiblePhases = useMemo(() =>
    allPhases.filter(ph => filterIds === null || filterIds.has(ph.phaseID)),
    [allPhases, filterIds]
  );
  const visibleMyTasks = useMemo(() =>
    myTasks.filter(t => filterIds === null || filterIds.has(t.ID)),
    [myTasks, filterIds]
  );
  const visibleNewTasks = useMemo(() =>
    newTasks.filter(t => filterIds === null || filterIds.has(t.ID)),
    [newTasks, filterIds]
  );

  const duplicateOlaNames = useMemo(() => {
    const counts = {};
    olasForProgramme.forEach(ola => { counts[ola.name] = (counts[ola.name] || 0) + 1; });
    return new Set(Object.keys(counts).filter(name => counts[name] > 1));
  }, [olasForProgramme]);

  const filledOlaCount = useMemo(() =>
    olasForProgramme.filter(ola => {
      const pref = olaPrefs[ola.ID];
      return pref && (pref.passionScore != null || pref.expertiseFit != null);
    }).length,
    [olasForProgramme, olaPrefs]
  );

  const progressPercent = olasForProgramme.length > 0
    ? Math.round((filledOlaCount / olasForProgramme.length) * 100)
    : 0;

  const filledPhaseCount = useMemo(() =>
    allPhases.filter(phase => phasePrefs[phase.phaseID]?.passionScore != null).length,
    [allPhases, phasePrefs]
  );

  const phaseProgressPercent = allPhases.length > 0
    ? Math.round((filledPhaseCount / allPhases.length) * 100)
    : 0;

  const filledTaskCount = useMemo(() =>
    myTasks.filter(t => taskPrefs[t.ID]?.wantsToDo != null).length,
    [myTasks, taskPrefs]
  );

  const taskProgressPercent = myTasks.length > 0
    ? Math.round((filledTaskCount / myTasks.length) * 100)
    : 0;

  useEffect(() => {
    setShowCompactProgress(false);
    const el = progressCardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowCompactProgress(!entry.isIntersecting),
      { rootMargin: "-97px 0px 0px 0px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, loading]);

  const toggleFilter = (items, getId, isUnfilled) => {
    if (filterIds !== null) { setFilterIds(null); return; }
    setFilterIds(new Set(items.filter(isUnfilled).map(getId)));
  };

  const toggleOlaFilter = () => toggleFilter(
    olasForProgramme,
    ola => ola.ID,
    ola => {
      const p = olaPrefs[ola.ID];
      return !p || (p.passionScore == null && p.expertiseFit == null);
    },
  );

  const togglePhaseFilter = () => toggleFilter(
    allPhases,
    ph => ph.phaseID,
    ph => phasePrefs[ph.phaseID]?.passionScore == null,
  );

  const toggleTaskFilter = () => toggleFilter(
    [...myTasks, ...newTasks],
    t => t.ID,
    t => taskPrefs[t.ID]?.wantsToDo == null,
  );

  if (loading) {
    return (
      <div className="text-center mt-5">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }

  if (!teacher) {
    return (
      <Container>
        <Alert variant="danger">Uw profiel kon niet worden geladen. Probeer opnieuw in te loggen.</Alert>
      </Container>
    );
  }

  if (noAcademicYear || !hoewistOpen) {
    return (
      <Container>
        <h1>Mijn voorkeuren</h1>
        <Alert variant="info">
          Het invullen van voorkeuren is momenteel niet beschikbaar. Probeer later opnieuw of neem contact op met uw opleidingsmanager.
        </Alert>
      </Container>
    );
  }

  if (programmes.length === 0) {
    return (
      <Container>
        <h1>Mijn voorkeuren</h1>
        <Alert variant="info">
          U bent momenteel niet gekoppeld aan een opleiding.
          Neem contact op met uw opleidingsmanager.
        </Alert>
      </Container>
    );
  }

  const currentProgress = isTaskTab
    ? { percent: taskProgressPercent,  label: "Voortgang taken",  filled: filledTaskCount,  total: myTasks.length }
    : isContextTab
      ? { percent: phaseProgressPercent, label: "Voortgang fasen",  filled: filledPhaseCount, total: allPhases.length }
      : { percent: progressPercent,      label: "Voortgang vakken", filled: filledOlaCount,   total: olasForProgramme.length };

  return (
    <Container className="pb-5 mb-5 px-2 px-md-3 hoewist-page">

      {showCompactProgress && (
        <button
          type="button"
          className="hoewist-progress-badge"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          title={`${currentProgress.label}: ${currentProgress.filled} / ${currentProgress.total}`}
        >
          <svg className="hoewist-progress-badge-ring" viewBox="0 0 36 36">
            <circle className="hoewist-progress-badge-track" cx="18" cy="18" r="15.9155" />
            <circle
              className="hoewist-progress-badge-fill"
              cx="18"
              cy="18"
              r="15.9155"
              style={{ strokeDasharray: `${currentProgress.percent}, 100` }}
            />
          </svg>
          <div className="hoewist-progress-badge-text">
            <span className="hoewist-progress-badge-percent">
              {currentProgress.percent}%
            </span>
            <span className="hoewist-progress-badge-count">
              {currentProgress.filled}/{currentProgress.total}
            </span>
          </div>
        </button>
      )}

      <Row className="mb-3 align-items-md-center g-2">
        <Col xs={12} md>
          <h1 className="mb-0">Mijn voorkeuren</h1>
          <p className="text-muted mb-0">Geef uw voorkeuren op voor vakken, fasen en taken.</p>
        </Col>
        {allSubmissions.length > 1 && (
          <Col xs={12} md="auto" className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 text-nowrap">Academiejaar:</Form.Label>
            <Form.Select
              className="flex-grow-1"
              value={selectedSubmissionId ?? ""}
              onChange={e => switchYear(e.target.value)}
            >
              {[...allSubmissions]
                .sort((a, b) => {
                  const yearA = academicYears.find(y => y.id === a.academicYearId)?.year ?? "";
                  const yearB = academicYears.find(y => y.id === b.academicYearId)?.year ?? "";
                  return yearB.localeCompare(yearA);
                })
                .map(sub => {
                  const year = academicYears.find(y => y.id === sub.academicYearId);
                  return year ? (
                    <option key={sub.id} value={sub.id}>
                      {year.year}{year.current_year ? " (huidig)" : ""}
                    </option>
                  ) : null;
                })}
            </Form.Select>
          </Col>
        )}
        <Col xs="auto" className="d-none d-md-block">
          <SaveButton unsaved={unsaved} saving={saving} onSave={save} />
        </Col>
      </Row>

      <Nav variant="tabs" className="mb-3 d-none d-md-flex">
        {programmes.map(p => (
          <Nav.Item key={p.ID}>
            <Nav.Link
              active={activeTab === p.ID}
              onClick={() => { setActiveTab(p.ID); setFilterIds(null); }}
              className="hoewist-programme-tab"
            >
              {p.name}
            </Nav.Link>
          </Nav.Item>
        ))}
        <Nav.Item>
          <Nav.Link
            active={isContextTab}
            onClick={() => { setActiveTab("contexten"); setFilterIds(null); }}
            className="hoewist-programme-tab"
          >
            <i className="far fa-map-marker-alt me-1" />
            Fasen
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={isTaskTab}
            onClick={() => { setActiveTab("taken"); setFilterIds(null); }}
            className="hoewist-programme-tab"
          >
            <i className="far fa-tasks me-1" />
            Taken
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="d-flex flex-column gap-2 d-md-none mb-3">
        <Form.Select
          value={isContextTab || isTaskTab ? "" : (activeTab ?? "")}
          onChange={e => { setActiveTab(parseInt(e.target.value, 10)); setFilterIds(null); }}
          aria-label="Kies opleiding"
        >
          {!isContextTab && !isTaskTab ? null : (
            <option value="" disabled>Kies opleiding…</option>
          )}
          {programmes.map(p => (
            <option key={p.ID} value={p.ID}>{p.name}</option>
          ))}
        </Form.Select>
        <div className="d-flex gap-2">
          <Button
            className="flex-grow-1"
            variant={isContextTab ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => { setActiveTab("contexten"); setFilterIds(null); }}
          >
            <i className="far fa-map-marker-alt me-1" />
            Fasen
          </Button>
          <Button
            className="flex-grow-1"
            variant={isTaskTab ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => { setActiveTab("taken"); setFilterIds(null); }}
          >
            <i className="far fa-tasks me-1" />
            Taken
          </Button>
        </div>
      </div>

      {!isTaskTab && !isContextTab && (
        <>
          <ProgressCard
            ref={progressCardRef}
            label="Voortgang vakken"
            filled={filledOlaCount}
            total={olasForProgramme.length}
            percent={progressPercent}
          />

          <Alert variant="info" className="mb-4">
            <strong>Disclaimer:</strong> We streven ernaar om bij de toewijzing van vakken rekening
            te houden met uw voorkeuren. In de praktijk kunnen we echter niet uitsluiten dat bepaalde vakken aan u worden toegewezen.
          </Alert>

          <h2 className="mb-1">Vakvoorkeuren</h2>
          <p className="text-muted mb-1">
            Geef op een schaal van 1 (Niet graag) tot 10 (Heel graag) aan hoe graag u elk vak geeft.
            Indien van toepassing, kunt u hieronder aangeven als dit vak minder goed aansluit bij uw huidige expertise.
          </p>
          <p className="text-muted mb-3">
            <i className="far fa-info-circle me-1" />
            U hoeft niet alle vakken in te vullen, vakken zonder voorkeur worden beschouwd als <strong>geen voorkeur opgegeven</strong>.
            U kunt tussendoor opslaan en later terugkomen om verder in te vullen.
          </p>

          <UnfilledFilterButton
            active={filterIds !== null}
            onToggle={toggleOlaFilter}
            labelAll="Toon alle vakken"
            labelUnfilled="Toon alleen niet-ingevulde vakken"
          />

          {olasForProgramme.length === 0 && (
            <Alert variant="warning">Geen vakken gevonden voor deze opleiding.</Alert>
          )}

          {visibleAssignedOlas.length > 0 && (
            <>
              <h5 className="text-muted mb-2">Mijn huidige vakken</h5>
              {visibleAssignedOlas.map(ola => (
                <OlaCard
                  key={ola.ID}
                  ola={ola}
                  pref={olaPrefs[ola.ID]}
                  onOlaPrefChange={updateOlaPref}
                  showOpo={duplicateOlaNames.has(ola.name)}
                />
              ))}
            </>
          )}

          {visibleUnassignedOlas.length > 0 && (
            <>
              <hr className="mt-5 mb-4" />
              <h5 className="mb-1">Overige vakken</h5>
              <p className="text-muted mb-3">
                Vakken binnen deze opleiding die momenteel niet aan u zijn toegewezen.
                Geef hier alvast uw voorkeur op als u interesse heeft.
              </p>
              {visibleUnassignedOlas.map(ola => (
                <OlaCard
                  key={ola.ID}
                  ola={ola}
                  pref={olaPrefs[ola.ID]}
                  onOlaPrefChange={updateOlaPref}
                  showOpo={duplicateOlaNames.has(ola.name)}
                />
              ))}
            </>
          )}

        </>
      )}

      {isContextTab && (
        <>
          <ProgressCard
            ref={progressCardRef}
            label="Voortgang fasen"
            filled={filledPhaseCount}
            total={allPhases.length}
            percent={phaseProgressPercent}
          />
          <h2 className="mb-1">Fasenvoorkeuren</h2>
          <p className="text-muted mb-3">
            Geef aan hoe graag u lesgeeft in elke fase (traject / campus).
          </p>
          <UnfilledFilterButton
            active={filterIds !== null}
            onToggle={togglePhaseFilter}
            labelAll="Toon alle fasen"
            labelUnfilled="Toon alleen niet-ingevulde fasen"
          />
          {allPhases.length === 0 && (
            <Alert variant="warning">Geen fasen gevonden.</Alert>
          )}
          {visiblePhases.map(phase => (
            <PhaseCard
              key={phase.phaseID}
              phase={phase}
              pref={phasePrefs[phase.phaseID]}
              onPhasePrefChange={updatePhasePref}
            />
          ))}
        </>
      )}

      {isTaskTab && (
        <>
          <UnfilledFilterButton
            active={filterIds !== null}
            onToggle={toggleTaskFilter}
            labelAll="Toon alle taken"
            labelUnfilled="Toon alleen niet-ingevulde taken"
          />

          <div className="mb-4">
            <h2 className="mb-1">Huidige taken</h2>
            <p className="text-muted mb-3">Wil je deze taken graag voortzetten?</p>
            {myTasks.length === 0 ? (
              <Alert variant="warning">U heeft momenteel geen taken toegewezen gekregen.</Alert>
            ) : (
              visibleMyTasks.map(task => (
                <TaskCard
                  key={task.ID}
                  task={task}
                  pref={taskPrefs[task.ID]}
                  isNew={false}
                  onChoiceChange={(id, wantsToDo) => updateTaskPref(id, { wantsToDo })}
                  onCommentChange={(id, comment) => updateTaskPref(id, { comment: comment || null })}
                />
              ))
            )}
          </div>

          {newTasks.length > 0 && (
            <div className="mb-4">
              <h2 className="mb-1">Nieuwe taken</h2>
              <p className="text-muted mb-3">
                Heb je ambities voor taken die je momenteel nog niet uitvoert?
              </p>
              {visibleNewTasks.map(task => (
                <TaskCard
                  key={task.ID}
                  task={task}
                  pref={taskPrefs[task.ID]}
                  isNew={true}
                  onChoiceChange={(id, wantsToDo) => updateTaskPref(id, { wantsToDo })}
                  onCommentChange={(id, comment) => updateTaskPref(id, { comment: comment || null })}
                />
              ))}
            </div>
          )}
        </>
      )}

      <div className="hoewist-sticky-bar">
        {unsaved
          ? <span className="text-warning hoewist-status-text">
              <i className="far fa-exclamation-circle" />&nbsp;Niet-opgeslagen wijzigingen
            </span>
          : <span className="text-success hoewist-status-text">
              <i className="far fa-check-circle" />&nbsp;Alles opgeslagen
            </span>
        }
        <SaveButton unsaved={unsaved} saving={saving} onSave={save} />
      </div>

    </Container>
  );
}

export default Hoewist;
